import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// New maximize icon
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

// New minimize icon
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';
const SubcategoryViewModal = ({ open, onClose, subcategory }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [updatedByUser, setUpdatedByUser] = useState(null);

  const [screenConfig, setScreenConfig] = useState(null);

  const SCREEN_NAME = 'Subcategories';

  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }

      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      console.log('Screen info.....', cfg);
      setScreenConfig(cfg);     // <-- IMPORTANT
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      throw err;
    }
  };

  // Helper to determine if Department should show in header
  const shouldShowDepartmentOnHeader = (screenConfig) => {
    if (!screenConfig) return false;

    const truthy = (v) => {
      if (v === undefined || v === null) return false;
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === '1' || s === 'true' || s === 'yes' || s === 'on';
      }
      return Boolean(v);
    };

    const showHeader = truthy(screenConfig.ShowOnHeader) || truthy(screenConfig.showOnHeader);
    const showScreen = truthy(screenConfig.ShowOnScreen) || truthy(screenConfig.showOnScreen);

    return showHeader && showScreen;
  };

  // Helper to determine if Department should show in items
  const shouldShowDepartmentOnItems = (screenConfig) => {
    if (!screenConfig) return false;

    const truthy = (v) => {
      if (v === undefined || v === null) return false;
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === '1' || s === 'true' || s === 'yes' || s === 'on';
      }
      return Boolean(v);
    };

    const showScreen = truthy(screenConfig.ShowOnScreen) || truthy(screenConfig.showOnScreen);
    const showItem = truthy(screenConfig.ShowOnItem) || truthy(screenConfig.showOnItem);

    return showScreen && showItem;
  };

  useEffect(() => {
    if (open && subcategory) {
      fetchScreenConfig();
      if (subcategory.CreatedBy) {
        fetchUserDetails(subcategory.CreatedBy, setCreatedByUser);
      }
      if (subcategory.UpdatedBy) {
        fetchUserDetails(subcategory.UpdatedBy, setUpdatedByUser);
      }
    }
  }, [open, subcategory]);

  const fetchUserDetails = async (userId, setUser) => {
    try {
      const response = await fetch(`${API_URL}/users/${userId}`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  if (!open || !subcategory) return null;
  const subcategoryItems = subcategory?.Items || [];

  const getStatusColor = (status) => {
    return status === 'Active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white';
  };

  const sections = [
    {
      title: 'Subcategory Information',
      fields: [
        { label: 'Subcategory Code', value: subcategory.SubcategoryCode },
        { label: 'Subcategory Name', value: subcategory.SubcategoryName },

        // ✅ Department (single field: Name ID Code inline)
        ...(shouldShowDepartmentOnHeader(screenConfig || subcategory?.screenConfig)
          ? [{
              label: 'Department',
              value: ` ${subcategory.DepartmentCode || '-'}`,
            }]
          : []),

        { label: 'Description', value: subcategory.Description || '-', isFullWidth: true },
        {
          label: 'Status',
          value: subcategory.Status === "1" ? 'Active' : 'Inactive',
          render: (value) => (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(value)}`}>
              {value}
            </span>
          )
        },
      ],
      name: 'Subcategory Info'
    },
    {
      title: 'Parent Category Information',
      fields: [
        { label: 'Parent Category', value: subcategory.ParentCategoryName || '-' },
        { label: 'Category Code', value: subcategory.ParentCategoryCode || '-' },
      ],
      name: 'Parent Category'
    },
    {
      title: 'System Information',
      fields: [
        {
          label: 'Created Date',
          value: subcategory.CreatedDate ? dayjs(subcategory.CreatedDate).format('YYYY-MM-DD HH:mm') : '-'
        },
        {
          label: 'Updated Date',
          value: subcategory.UpdatedDate ? dayjs(subcategory.UpdatedDate).format('YYYY-MM-DD HH:mm') : '-'
        },
        {
          label: 'Created By',
          value: createdByUser?.FullName || createdByUser?.Username || subcategory.CreatedBy || '-'
        },
        {
          label: 'Updated By',
          value: updatedByUser?.FullName || createdByUser?.Username || subcategory.UpdatedBy || '-'
        },
      ],
      name: 'System Info'
    }
  ];
// Navigation functions
  const handleNext = () => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  // Toggle maximize/minimize (needed by the header button)
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
      ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
        .hide-scrollbar {
          -ms-overflow-style: none; /* IE 10+ */
          scrollbar-width: none; /* Firefox */
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none; /* Safari & Chrome */
        }
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}
      </style>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

      {/* Panel */}
      <div
        className={`bg-white shadow-2xl h-full flex flex-col
        transition-transform transform-gpu duration-300 ease-in-out
        ${open ? 'translate-x-0' : 'translate-x-full'}
        ${isMaximized ? 'w-full max-w-full' : 'w-full max-w-6xl'}`}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#6A89A7] rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#384959]">Subcategory Details</h2>
                <p className="text-[#64748b]">{subcategory.SubcategoryCode}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]"
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto scrollbar px-8 pb-6 space-y-6">
          {sections.map((section, index) => (
            <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">{section.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field, fieldIndex) => (
                  <div key={fieldIndex} className={`flex flex-col ${field.isFullWidth ? 'md:col-span-2' : ''}`}>
                    <span className="text-sm font-medium text-[#64748b]">{field.label}</span>
                    <span className="text-base text-[#384959] mt-1 font-semibold break-words whitespace-normal uppercase">
                      {field.render ? field.render(field.value) : field.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          

        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryViewModal;